import { Injectable } from '@angular/core';
import { HttpService } from './http/http.service';
import { firstValueFrom } from 'rxjs';
import { CustomAttribute } from '../interface';

@Injectable({
  providedIn: 'root'
})
export class CustomAttributesService {

  /**
   * Attributes seen from any fetch, keyed by id. Lets presentational components resolve an
   * attribute's dropdown options from an attributeId alone, without every form that builds a
   * custom-attribute FormGroup having to thread the options through.
   */
  private _cache = new Map<number, CustomAttribute>();

  constructor(private _http: HttpService) { }

  public async getByModule(moduleId: number): Promise<CustomAttribute[]> {
    return this._cacheAll(await firstValueFrom(this._http.get('/CustomAttributes/Module/' + moduleId)));
  }
  public async get(id: number): Promise<CustomAttribute> {
    const attribute: CustomAttribute = await firstValueFrom(this._http.get('/CustomAttributes/' + id));
    if (attribute) this._cache.set(attribute.id, attribute);
    return attribute;
  }

  public async getAll(): Promise<CustomAttribute[]> {
    return this._cacheAll(await firstValueFrom(this._http.get('/CustomAttributes/')));
  }

  /** Synchronous lookup of an already-fetched attribute. Undefined if it was never loaded. */
  public getCached(id: number): CustomAttribute | undefined {
    return this._cache.get(id);
  }

  public create(data: any): Promise<CustomAttribute> {
    return firstValueFrom(this._http.post('/CustomAttributes', data));
  }

  public update(id: number, data: any): Promise<CustomAttribute> {
    return firstValueFrom(this._http.patch('/CustomAttributes/', id, data));
  }
  public delete(id: number): Promise<CustomAttribute> {
    this._cache.delete(id);
    return firstValueFrom(this._http.delete('/CustomAttributes/', id));
  }

  private _cacheAll(attributes: CustomAttribute[]): CustomAttribute[] {
    attributes?.forEach(a => this._cache.set(a.id, a));
    return attributes;
  }
}
